import { z } from "zod";
import { type SexClass } from "@/benchmarks/BenchmarkFilters/SexPicker";
import { type graphql } from "@/graphql";
import { createOptionalStringSchema, createRequiredStringSchema } from "@/lib/utils/validation/zod";

export type LotStatus = ReturnType<typeof graphql.scalar<"SupplyChainStatusEnumType">>;

const sexClassEnum = ["STEER", "HEIFER", "MIXED"] as const satisfies readonly SexClass[];
const statusEnum = ["OPEN", "CLOSED"] as const satisfies readonly LotStatus[];

const createOptionalDecimalPercentageSchema = ({
  displayName,
  decimalPlaces = 2,
}: {
  displayName: string;
  decimalPlaces?: number;
}) => {
  return z.preprocess(
    (val) => {
      if (val === "" || val === undefined || val === null) return undefined;
      return String(val);
    },
    z
      .string()
      .optional()
      .refine(
        (val) => {
          if (!val) return true; // optional field
          const num = Number(val);
          return !isNaN(num);
        },
        { message: `${displayName} must be a valid number.` },
      )
      .refine(
        (val) => {
          if (!val) return true;
          const num = Number(val);
          return num >= 0;
        },
        { message: `${displayName} must be at least 0%.` },
      )
      .refine(
        (val) => {
          if (!val) return true;
          const num = Number(val);
          return num <= 100;
        },
        { message: `${displayName} cannot exceed 100%.` },
      )
      .refine(
        (val) => {
          if (!val) return true;
          const decimalPart = val.split(".")[1];
          return !decimalPart || decimalPart.length <= decimalPlaces;
        },
        { message: `${displayName} can have at most ${decimalPlaces} decimal places.` },
      ),
  );
};

export const lotFormSchema = z
  .object({
    sexClass: z.enum(sexClassEnum, {
      message: "Sex class is required.",
    }),
    status: z.enum(statusEnum, {
      message: "Status is required.",
    }),
    reference: createRequiredStringSchema("Lot ID", 100),
    sortNote: createOptionalStringSchema("Sort Note", 500).optional(),
    sourcePartner: z.uuid({ message: "Invalid source partner ID" }).nullish(),
    feedyardPartner: z.uuid({ message: "Invalid feedyard partner ID" }).nullish(),
    destinationPartner: z.uuid({ message: "Invalid destination partner ID" }).nullish(),
    animalBreeds: z.array(z.uuid({ message: "Invalid breed ID" })).optional(),
    weightTotalIn: z.string().optional(),
    weightTotalOut: z.string().optional(),
    weightTotalFeedIntake: z.string().optional(),
    purchasePrice: z.string().optional(),
    salePrice: z.string().optional(),
    feedCost: z.string().optional(),
    nonFeedCost: z.string().optional(),
    primeFraction: createOptionalDecimalPercentageSchema({ displayName: "Prime" }),
    choiceFraction: createOptionalDecimalPercentageSchema({ displayName: "Choice" }),
    selectFraction: createOptionalDecimalPercentageSchema({ displayName: "Select" }),
    numTotalIn: z.string().optional(),
    numTotalOut: z.string().optional(),
    numTotalDead: z.string().optional(),
    numTotalRemoved: z.string().optional(),
    dateOpened: z.date().optional(),
    dateClosed: z.date().optional(),
  })
  .refine(
    (data) => {
      if (data.dateOpened && data.dateClosed) {
        return data.dateClosed >= data.dateOpened;
      }
      return true;
    },
    {
      message: "Closed date cannot be before opened date.",
      path: ["dateClosed"],
    },
  )
  .refine((data) => !(data.status === "OPEN" && data.dateClosed), {
    message: "Open lots cannot have a closed date.",
    path: ["dateClosed"],
  });

export type LotFormSchema = z.infer<typeof lotFormSchema>;

export const lotFormDefaults = {
  reference: "",
  sexClass: "MIXED",
  status: "OPEN",
  sortNote: "",
  animalBreeds: [],
  weightTotalIn: "",
  weightTotalOut: "",
  weightTotalFeedIntake: "",
  purchasePrice: "",
  salePrice: "",
  feedCost: "",
  nonFeedCost: "",
  primeFraction: "",
  choiceFraction: "",
  selectFraction: "",
  numTotalIn: "",
  numTotalOut: "",
  numTotalDead: "",
  numTotalRemoved: "",
  dateOpened: undefined,
  dateClosed: undefined,
} satisfies Partial<LotFormSchema>;
